import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DateAdapter } from '@angular/material/core';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { environment, SupportedLanguage } from '@rms/env';
import { DateTime, Settings } from 'luxon';
import { BehaviorSubject } from 'rxjs';

import { LoggerService } from '../logger/logger.service';
import { LUXON_LOCALE_MAPPER } from '../luxon/luxon-locale-mapper.const';
import { DEFAULT_LANGUAGE } from './default-language.const';
import { LANGUAGE_TOKEN } from './language-storage-token.const';

@Injectable({
  providedIn: 'root',
})
export class LanguageDetectionService {
  private readonly translateService = inject(TranslateService);
  private readonly adapter = inject<DateAdapter<DateTime>>(DateAdapter);
  private readonly destroy$ = inject(DestroyRef);
  private readonly logger = inject(LoggerService);

  public languageDetectionComplete = new BehaviorSubject<boolean>(false);

  constructor() {
    this.detectLanguage();
    this.translateService.onLangChange.pipe(takeUntilDestroyed(this.destroy$)).subscribe((e: LangChangeEvent) => {
      const match = this.findSupported(e.lang);
      this.updateDateLocale(match.locale);
      this.storeLanguage(match.locale);
    });
  }

  public storeLanguage(lang: string): void {
    try {
      localStorage.setItem(LANGUAGE_TOKEN, lang);
    } catch (error) {
      this.logger.error(error, 'LanguageDetectionService.storeLanguage failed to access LocalStorage');
    }
  }

  private detectLanguage(): void {
    let storageLanguage = null;
    try {
      storageLanguage = localStorage.getItem(LANGUAGE_TOKEN);
    } catch (error) {
      this.logger.error(
        error,
        'LanguageDetectionService.detectLanguage failed to access LocalStorage, fallback to browser default'
      );
    }
    const browserLanguage: SupportedLanguage = this.findSupported(this.translateService.getBrowserLang());

    if (storageLanguage) {
      this.setTranslateLanguage(this.findSupported(storageLanguage));
    } else {
      this.setTranslateLanguage(browserLanguage);
    }
  }

  private findSupported(lang: string | undefined): SupportedLanguage {
    return environment.supportedLanguages.find((l: SupportedLanguage) => l.locale === lang) || DEFAULT_LANGUAGE;
  }

  private setTranslateLanguage(lang: SupportedLanguage): void {
    if (lang.locale !== this.translateService.getCurrentLang()) {
      this.translateService.setFallbackLang(environment.defaultLanguage);
      this.translateService.use(lang.locale).subscribe(() => {
        this.languageDetectionComplete.next(true);
      });
    } else {
      this.languageDetectionComplete.next(true);
    }
  }

  private updateDateLocale(localeId: string): void {
    const locale = LUXON_LOCALE_MAPPER[localeId];
    Settings.defaultLocale = locale;
    this.adapter.setLocale(locale);
  }
}
